import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowLeft, ArrowUp, HelpCircle } from "lucide-react";
import { Link } from "wouter";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/hooks/use-auth";
import { orpc } from "@/lib/rpc";

const STATUS_LABELS: Record<string, string> = {
  waiting_approval: "Aguardando aprovação",
  approved: "Aprovada",
  rejected: "Rejeitada",
  sent: "Enviada",
  accepted: "Aceita",
  declined: "Recusada",
};

function formatBrl(value: string | number | null): string {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatDate(value: string | Date | null): string {
  if (!value) return "—";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" });
}

function pct(n: number, total: number): string {
  if (total === 0) return "0%";
  return `${((n / total) * 100).toFixed(0)}%`;
}

function rate(accepted: number, declined: number): string {
  const closed = accepted + declined;
  if (closed === 0) return "—";
  return `${((accepted / closed) * 100).toFixed(0)}%`;
}

export default function OfferRecommendationFeedback() {
  const { isSuperAdmin } = useAuth();
  const canAccess = isSuperAdmin;

  const { data, isLoading, error } = useQuery(
    orpc.offerRecommendationFeedbackLoop.getDashboard.queryOptions(),
  );

  if (!canAccess) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Você não tem permissão para acessar esta página.
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        Carregando dados do feedback loop…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center text-sm text-rose-700">
        Falha ao carregar dados. {error instanceof Error ? error.message : ""}
      </div>
    );
  }

  const { summary, offerDetails } = data;

  const excitementTotal = summary.excitementAcceptedCount + summary.excitementOverriddenCount;
  const overallAcceptanceRate = rate(summary.acceptedCount, summary.declinedCount);

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-2">
        <Link href="/labs/candidate-intelligence">
          <Button variant="ghost" size="icon" data-testid="back-to-labs">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <PageHeader
          title="Feedback Loop — Offer Recommendations"
          description="Calibração das skills de excitement e recommendation com base em decisões do HM e outcomes do candidato"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard
          label="Total de Ofertas"
          value={summary.totalOffers}
          data-testid="stat-total-offers"
        />
        <StatCard
          label="Com Recomendação IA"
          value={summary.offersWithRecommendation}
          sub={pct(summary.offersWithRecommendation, summary.totalOffers)}
          data-testid="stat-with-rec"
        />
        <StatCard
          label="Taxa de Aceitação Geral"
          value={overallAcceptanceRate}
          sub={`${summary.acceptedCount} aceitas / ${summary.declinedCount} recusadas`}
          data-testid="stat-acceptance-rate"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CalibrationCard
          title="Excitement Score"
          tooltip="Mede a calibração da skill de excitement: quantas vezes o HM manteve o score da IA versus quantas vezes ele ajustou (com justificativa)."
          testId="card-excitement"
          left={{
            label: "HM aceitou",
            value: summary.excitementAcceptedCount,
            sub: pct(summary.excitementAcceptedCount, excitementTotal),
            tone: "neutral",
          }}
          right={{
            label: "HM ajustou",
            value: summary.excitementOverriddenCount,
            sub: pct(summary.excitementOverriddenCount, excitementTotal),
            tone: "amber",
          }}
        />
        <CalibrationCard
          title="Valores da Oferta"
          tooltip="Mede a calibração da recomendação de salary e equity: quantas vezes o HM enviou a oferta como recomendado versus quantas vezes editou os valores (com justificativa)."
          testId="card-values"
          left={{
            label: "HM seguiu",
            value: summary.hmFollowedCount,
            sub: pct(summary.hmFollowedCount, summary.offersWithRecommendation),
            tone: "neutral",
          }}
          right={{
            label: "HM editou",
            value: summary.hmDeviatedCount,
            sub: pct(summary.hmDeviatedCount, summary.offersWithRecommendation),
            tone: "amber",
          }}
        />
        <CalibrationCard
          title="Aceitação do Candidato"
          tooltip="Taxa de aceitação dividida pelo comportamento do HM com a recomendação. Considera apenas ofertas já aceitas ou recusadas — pendentes ficam de fora."
          testId="card-acceptance"
          left={{
            label: "HM seguiu oferta",
            value: rate(summary.acceptedWhenFollowed, summary.declinedWhenFollowed),
            sub: `${summary.acceptedWhenFollowed} / ${summary.acceptedWhenFollowed + summary.declinedWhenFollowed}`,
            tone: "emerald",
          }}
          right={{
            label: "HM editou oferta",
            value: rate(summary.acceptedWhenDeviated, summary.declinedWhenDeviated),
            sub: `${summary.acceptedWhenDeviated} / ${summary.acceptedWhenDeviated + summary.declinedWhenDeviated}`,
            tone: "amber",
          }}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Detalhamento por Oferta ({offerDetails.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data</TableHead>
                <TableHead>Candidato</TableHead>
                <TableHead>Vaga</TableHead>
                <TableHead>Nível</TableHead>
                <TableHead className="text-center">Score IA</TableHead>
                <TableHead className="text-center">Score Final</TableHead>
                <TableHead>Motivo Override</TableHead>
                <TableHead className="text-right">Recomendado</TableHead>
                <TableHead className="text-right">Oferecido</TableHead>
                <TableHead>Outcome</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {offerDetails.map((o) => {
                const original = o.excitementScoreOriginal;
                const final = o.excitementScoreFinal;
                const overridden = original !== null && final !== null && final !== original;
                return (
                  <TableRow key={o.offerId} data-testid={`offer-row-${o.offerId}`}>
                    <TableCell className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                      {formatDate(o.createdAt)}
                    </TableCell>
                    <TableCell className="font-medium max-w-[160px] truncate">
                      {o.candidateName}
                    </TableCell>
                    <TableCell className="max-w-[140px] truncate">{o.jobTitle}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{o.jobLevel}</Badge>
                    </TableCell>
                    <TableCell className="text-center tabular-nums">{original ?? "—"}</TableCell>
                    <TableCell className="text-center tabular-nums">
                      {final === null ? (
                        "—"
                      ) : overridden ? (
                        <span className="inline-flex items-center gap-1 text-amber-600 font-medium">
                          {final}
                          {final > (original ?? 0) ? (
                            <ArrowUp className="h-3 w-3" />
                          ) : (
                            <ArrowDown className="h-3 w-3" />
                          )}
                        </span>
                      ) : (
                        final
                      )}
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      {o.overrideReason ? (
                        <span
                          className="block truncate text-xs text-muted-foreground"
                          title={o.overrideReason}
                        >
                          {o.overrideReason}
                        </span>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatBrl(o.recommendedSalary)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatBrl(o.offeredSalary)}
                    </TableCell>
                    <TableCell>
                      <OutcomeBadge status={o.offerStatus} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {offerDetails.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                    Nenhuma oferta encontrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  label,
  value,
  sub,
  ...props
}: {
  label: string;
  value: string | number;
  sub?: string;
} & React.HTMLAttributes<HTMLDivElement>) {
  return (
    <Card {...props}>
      <CardContent className="pt-4 pb-3 px-4">
        <div className="text-xs text-muted-foreground mb-1">{label}</div>
        <div className="text-2xl font-bold tabular-nums">{value}</div>
        {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
      </CardContent>
    </Card>
  );
}

type Tone = "neutral" | "amber" | "emerald";

interface MetricBox {
  label: string;
  value: string | number;
  sub?: string;
  tone: Tone;
}

const TONE_CLASSES: Record<Tone, string> = {
  neutral: "text-foreground",
  amber: "text-amber-600",
  emerald: "text-emerald-600",
};

function CalibrationCard({
  title,
  tooltip,
  left,
  right,
  testId,
}: {
  title: string;
  tooltip: string;
  left: MetricBox;
  right: MetricBox;
  testId?: string;
}) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-1.5">
          {title}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`Mais informações sobre ${title}`}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <HelpCircle className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="text-xs leading-relaxed max-w-xs" side="top">
              {tooltip}
            </PopoverContent>
          </Popover>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-3">
        <MetricBoxView box={left} />
        <MetricBoxView box={right} />
      </CardContent>
    </Card>
  );
}

function MetricBoxView({ box }: { box: MetricBox }) {
  return (
    <div className="rounded-md border p-3">
      <div className="text-xs text-muted-foreground">{box.label}</div>
      <div className={`text-xl font-semibold tabular-nums ${TONE_CLASSES[box.tone]}`}>
        {box.value}
      </div>
      {box.sub && <div className="text-xs text-muted-foreground">{box.sub}</div>}
    </div>
  );
}

function OutcomeBadge({ status }: { status: string }) {
  const label = STATUS_LABELS[status] ?? status;
  if (status === "accepted") {
    return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">{label}</Badge>;
  }
  if (status === "declined") {
    return <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">{label}</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{label}</Badge>;
}
